// @flow strict
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/no-danger */
import React from 'react';
import styledImport from 'styled-components';
const styled = styledImport.default || styledImport;
import { Box, Flex } from '../../../../../../primitives/Essentials/index.js';
import Line from '../../../../../../primitives/Line/index.js';
import mq from '../../../../../../services/mediaQuery/index.js';
import type { EditorialSection as EditorialSectionType } from '../../../../../../records/EditorialContent.js';
import Image from '../../../../../Image/index.js';
import PhotoAttribution from '../../../../../PhotoAttribution/index.js';
import { EDITORIAL_IMAGE_HEIGHT, EDITORIAL_IMAGE_WIDTH } from '../../../../constants.js';

const StyledBox = styled(Box)`
  padding-right: 0;
  padding-left: 0;

  &:not(&:last-child) {
    padding-bottom: 15px;
  }

  ${mq.TABLET`
    padding-right: 0;
    padding-left: 0;
    &:not(&:last-child) {
      padding-bottom: 0;
    }
    &:not(&:nth-last-of-type(-n+2)) {
      padding-bottom: 15px;
    }
    &:nth-child(2n) {
      padding-left: 7.5px;
    }
    &:nth-child(2n + 1) {
      padding-right: 7.5px;
    }
  `}
`;

const StyledParagraphHtml = styled.div`
  margin-bottom: 15px;
  color: ${({ theme }) => theme.trip.abstract};
  font-size: 16px;
  letter-spacing: -0.02px;
  line-height: 1.38;
  a {
    color: inherit;
  }
`;

const StyledImg = styled(Image).attrs(({ $width, $height }) => ({
  width: $width,
  height: $height,
}))`
  display: block;
  width: 100%;
  min-width: 100%;
  height: auto;
  min-height: 194px;
  border-radius: 5px;
  ${mq.SMALL_MOBILE`
    min-height: 211px;
  `};
  ${mq.MIDDLE_MOBILE`
    min-height: 247px;
  `};
  ${mq.BIG_MOBILE`
    min-height: 372px;
  `};
  ${mq.TABLET`
    min-height: 227px;
  `};
  ${mq.DESKTOP`
    min-height: 379px;
  `};
`;

const StyledImageLine = styled(Line).withConfig({
  shouldForwardProp: (prop) => prop !== 'theme', // Allow all props except 'theme'
})`
  color: ${({ theme }) => theme.trip.divider};
  text-align: center;
`;

const StyledH2 = styled.h2`
  margin: 0 0 6px;
  color: ${({ theme }) => theme.section.title};
  font-weight: 900;
  font-size: 22px;
`;

const StyledH3 = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.section.title};
  font-weight: 900;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.03px;
`;
type Props = {
  section: EditorialSectionType,
  lazyImages?: boolean,
};

const EditorialSection = ({ section, lazyImages = true }: Props) => (
  <StyledBox width={[1, null, null, null, 1 / 2]}>
      {section.photo && (
        <Flex $flexDirection="column" $mb={14.5}>
          <Box $mb={8}>
            <StyledImg
              src={section.photo.photoUrls[0]}
              srcSet={section.photo.photoUrls[1] ? `${section.photo.photoUrls[1]} 2x` : undefined}
              alt={section.photo.photoTitle}
              $width={EDITORIAL_IMAGE_WIDTH}
              $height={EDITORIAL_IMAGE_HEIGHT}
              plain
              lazy={lazyImages}
            />
          </Box>
          <StyledImageLine fontSize={14}>
            {section.photo.photoTitle}
            <PhotoAttribution attribution={section.photo.photoAttribution} />
          </StyledImageLine>
        </Flex>
      )}
      <>
        {section.title && <StyledH2>{section.title}</StyledH2>}
        <>
          {section.abstract && (
            <StyledParagraphHtml
              dangerouslySetInnerHTML={ { __html: section.abstract } }
            />
          )}
        </>
        <>
          {section.paragraphs &&
            section.paragraphs.map((paragraph, i) => (
            <Box key={`par-${i}`}>
              <StyledH3>{paragraph.title}</StyledH3>
              <StyledParagraphHtml
                dangerouslySetInnerHTML={{ __html: paragraph.abstract }}
              />
            </Box>
          ))}
        </>
      </>
  </StyledBox>
);

export default EditorialSection;
